<template>
  <div
    class="input-holder"
    :class="[
      {
        disabled: isDisabled,
        'has-label': label,
        'has-icons-left': icon && iconLocation === 'left',
        'has-icons-right': icon && iconLocation === 'right',
        'is-active': active,
      },
      suffix ? `suffix ${suffix}-suffix` : '',
    ]"
    v-b-tooltip="tooltip"
  >
    <label v-if="label" :class="['rbz-label', { disabled: isDisabled }]">
      {{ label }}
      <slot name="postLabel" />
    </label>
    <input
      :type="type"
      :data-qa="dataQa"
      :value="localInputValue"
      :class="['form-input', 'rbz-text-regular', classes, errorMessage?.class]"
      :placeholder="placeholder"
      :name="name"
      :max="max"
      :disabled="isDisabled"
      :readonly="readonly"
      v-bind="id ? { id } : {}"
      ref="inputElement"
      autocomplete="off"
      @focus="inputFocus($event)"
      @blur="inputBlur($event)"
      @keydown="onKeyDown($event)"
      @input="onInput($event)"
      @change="onChange($event)"
      tabindex="0"
    />
    <component
      v-if="inputIcon && iconLocation"
      :is="inputIcon"
      v-bind="iconProps"
      color="dusty-gray"
      :class="{
        'is-left': iconLocation === 'left',
        'is-right': iconLocation === 'right',
        'is-clickable': iconClickable,
      }"
      class="input-icon"
      data-qa="input-icon"
      @click.stop="$emit('iconClicked')"
    />
    <span v-if="errorMessage?.message" class="error-message rbz-text-small">
      {{ errorMessage.message }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { getIconComponent } from '../../assets/icons';
import { GenericObject } from '../../rbz/types';

export type InputType = 'text' | 'password' | 'email' | 'number' | 'color';
export type NumberType = 'whole' | 'decimal';

interface FormInputProps {
  name: string;
  label?: string;
  type?: InputType;
  numberType?: NumberType;
  classes?: string;
  placeholder?: string;
  inputValue: string | number;
  required?: boolean;
  max?: number;
  disabled?: boolean;
  isUpdateInProcess?: boolean;
  ignoreUpdateInProcess?: boolean;
  suffix?: string;
  isInvalid?: boolean;
  errorMessageText?: string;
  tooltip?: string;
  dataQa?: string;
  readonly?: boolean;
  id?: string;
  icon?: string;
  iconLocation?: 'left' | 'right';
  iconClickable?: boolean;
  iconProps?: GenericObject;
  autoFocus?: boolean;
}

const props = withDefaults(defineProps<FormInputProps>(), {
  label: '',
  type: 'text',
  numberType: 'whole',
  placeholder: '',
  classes: '',
  tooltip: '',
  dataQa: '',
  required: false,
  disabled: false,
  suffix: '',
  max: null,
  errorMessageText: '',
  id: '',
  icon: null,
  iconLocation: 'left',
  iconClickable: false,
  iconProps: null,
  autoFocus: false,
});

interface FormInputEmits {
  (e: 'update:inputValue', value: string | number): void;

  (e: 'change:inputValue', value: string | number): void;

  (e: 'iconClicked'): void;

  (e: 'focus', value: Event): void;

  (e: 'blur', value: Event): void;
}

const emit = defineEmits<FormInputEmits>();

const localInputValue = ref(props.inputValue);
watch(
  () => props.inputValue,
  async () => {
    localInputValue.value = props.inputValue;
  }
);

const isDirty = ref(false);
watch(
  () => props.inputValue,
  async () => {
    isDirty.value = true;
  }
);

const isDisabled = computed(() => {
  return props.disabled || (props.isUpdateInProcess && !props.ignoreUpdateInProcess);
});

const inputIcon = computed(() => {
  if (props.icon) return getIconComponent(props.icon);
  return '';
});

const errorMessage = computed(() => {
  if (props.isInvalid && isDirty.value) {
    return {
      class: 'error',
      message: props.errorMessageText,
    };
  }

  return {
    class: '',
    message: '',
  };
});
const active = ref(false);
const inputFocus = (event: Event) => {
  active.value = true;
  emit('focus', event);
};
const inputBlur = (event: Event) => {
  active.value = false;
  emit('blur', event);
};
const onKeyDown = (event: KeyboardEvent) => {
  if (props.type === 'number') {
    if (props.numberType === 'whole') {
      if (/^(\.|e|E)$/.test(event.key)) {
        event.preventDefault();
      }
    }
  }
};
const onInput = (event: Event) => {
  let value: string | number = (event.target as HTMLInputElement).value;
  if (props.type === 'number') {
    value = Number(value);
  }
  if (props.type === 'text') {
    value = (value as string).trim(); // Trim for text inputs
  }
  emit('update:inputValue', value);
};
const onChange = (event: Event) => {
  let value: string | number = (event.target as HTMLInputElement).value;
  if (props.type === 'number') {
    value = Number(value);
  }
  if (props.type === 'text') {
    value = (value as string).trim(); // Trim for text inputs
  }
  emit('change:inputValue', value);
};

const inputElement = ref(null);
const focus = () => {
  inputElement.value.focus();
};

const setIsDirty = (value: boolean) => {
  isDirty.value = value;
};

onMounted(async () => {
  if (props.autoFocus) {
    focus();
  }
});

defineExpose({
  focus,
  setIsDirty,
});
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '../../assets/styles/colors' as colors;

$form-input-height: 40px;

input {
  outline: none;
}

.input-holder {
  display: flex;
  flex-direction: column;
  position: relative;

  .form-input {
    background: map.get(colors.$colors, 'white');
    border: 1px solid map.get(colors.$colors, 'silver');
    border-radius: 4px;
    box-shadow: 0 0 0 rgba(0, 0, 0, 0.25);
    box-sizing: border-box;
    color: map.get(colors.$colors, 'shuttle-gray');
    height: $form-input-height;
    padding: 10px 12px;
    width: 100%;

    /* Chrome, Firefox, Opera, Safari 10.1+ */
    &::placeholder {
      /* stylelint-disable-line selector-no-vendor-prefix */
      color: map.get(colors.$colors, 'dusty-gray');
      opacity: 1; /* Firefox */
    }

    /* Microsoft Edge */
    /* stylelint-disable-next-line selector-no-vendor-prefix, selector-pseudo-element-no-unknown */
    &::-ms-input-placeholder {
      color: map.get(colors.$colors, 'dusty-gray');
    }

    /* Internet Explorer 10-11 */
    /* stylelint-disable-next-line selector-no-vendor-prefix, selector-pseudo-class-no-unknown */
    &:-ms-input-placeholder {
      color: map.get(colors.$colors, 'dusty-gray');
    }
  }

  .form-input:hover {
    background: map.get(colors.$colors, 'selago');
    border: 1px solid map.get(colors.$colors, 'silver');
    box-shadow: 0 0 0 rgba(0, 0, 0, 0.25);
  }

  &.is-active {
    .form-input,
    .form-input:focus {
      border: 1px solid map.get(colors.$colors, 'jewel');
      box-shadow: 0 0 0 rgba(0, 0, 0, 0.25);
      outline: none;
    }

    .form-input + :deep(.input-icon) {
      color: map.get(colors.$colors, 'shuttle-gray');
    }
  }

  &.has-icons-left,
  &.has-icons-right {
    :deep(.input-icon) {
      border: 0;
      bottom: 8px;
      position: absolute;
    }

    :deep(.input-icon.is-clickable) {
      cursor: pointer;
      pointer-events: all;
    }
  }

  &.has-icons-left {
    .form-input {
      padding-left: 44px;
    }

    :deep(.input-icon.is-left) {
      left: 12px;
    }
  }

  &.has-icons-right {
    .form-input {
      padding-right: 44px;
    }

    :deep(.input-icon.is-right) {
      right: 12px;
    }
  }

  .error {
    background: map.get(colors.$colors, 'white');
    border: 1px solid map.get(colors.$colors, 'cinnabar');
    border-radius: 4px;
    box-shadow: 0 0 0 rgba(0, 0, 0, 0.25);
  }

  .error-message {
    color: map.get(colors.$colors, 'flamingo');
    margin-top: 4px;
  }
}

.input-holder.suffix {
  &.seconds-suffix,
  &.minutes-suffix {
    .form-input {
      padding-right: 75px;
    }
  }

  &.hours-suffix {
    .form-input {
      padding-right: 55px;
    }
  }

  &.mb-suffix,
  &.kilobytes-suffix {
    .form-input {
      padding-right: 35px;
    }
  }

  &::before {
    color: map.get(colors.$colors, 'shuttle-gray');
    display: block;
    position: absolute;
    right: 10px;
    top: calc($form-input-height / 2);
    transform: translateY(-50%);
  }

  &.has-label::before {
    top: calc(($form-input-height / 2) + 21px);
  }

  &.seconds-suffix::before {
    content: 'seconds';
  }

  &.minutes-suffix::before {
    content: 'minutes';
  }

  &.hours-suffix::before {
    content: 'hours';
  }

  &.kilobytes-suffix::before {
    content: 'KB';
  }

  &.mb-suffix::before {
    content: 'MB';
  }
}

.input-holder.disabled {
  pointer-events: none;

  .form-input {
    background: map.get(colors.$colors, 'white');
    border: 1px solid map.get(colors.$colors, 'silver');
    border-radius: 4px;
    box-shadow: 0 0 0 rgba(0, 0, 0, 0.25);
    color: map.get(colors.$colors, 'silver');

    /* Chrome, Firefox, Opera, Safari 10.1+ */
    &::placeholder {
      /* stylelint-disable-line selector-no-vendor-prefix */
      color: map.get(colors.$colors, 'silver');
      opacity: 1; /* Firefox */
    }

    /* Microsoft Edge */
    /* stylelint-disable-next-line selector-no-vendor-prefix, selector-pseudo-element-no-unknown */
    &::-ms-input-placeholder {
      color: map.get(colors.$colors, 'silver');
    }

    /* Internet Explorer 10-11 */
    /* stylelint-disable-next-line selector-no-vendor-prefix, selector-pseudo-class-no-unknown */
    &:-ms-input-placeholder {
      color: map.get(colors.$colors, 'silver');
    }
  }

  input:hover {
    background: map.get(colors.$colors, 'white');
  }

  &.suffix::before {
    color: map.get(colors.$colors, 'silver');
  }
}
</style>
